import React, { useEffect, useMemo, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'

import useTranslation from '@/Hooks/UseTranslation'
import ContentBlock from '@/Components/ProgrammationManager/components/ContentBlock'
import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import Times from '@/Components/SvgImages/Times'
import {
  CommitmentFailure,
  ProjectCncCatalogRow,
} from '@/Services/CncLearningService'

import '../ProgrammationManager/ProgrammationManager.scss'
import './WeeklyFailureAnalysisModal.scss'

interface Props {
  show: boolean
  failure: CommitmentFailure | null
  taskName: string
  catalog: ProjectCncCatalogRow[]
  members: any[]
  saving: boolean
  error?: string
  onHide: () => void
  onAnalyze: (input: { primaryCauseId: string, explanation: string, rootCause: string }) => Promise<void>
  onCreateAction: (input: { title: string, description: string, owner: string, dueDate: string }) => Promise<void>
}

const causeGroups = [
  [ 'obra_construccion', 'CNC_SELECTOR:ProjectTab' ],
  [ 'diseno_oficina_tecnica_de_obra', 'CNC_SELECTOR:DesignTab' ],
  [ 'administracion', 'CNC_SELECTOR:AdministrationTab' ],
  [ 'logistica', 'CNC_SELECTOR:LogisticsTab' ],
  [ 'condiciones_externas', 'CNC_SELECTOR:ExternalConditionsTab' ],
]

const WeeklyFailureAnalysisModal = ({
  show,
  failure,
  taskName,
  catalog,
  members,
  saving,
  error,
  onHide,
  onAnalyze,
  onCreateAction,
}: Props) => {
  const { t } = useTranslation()
  const [ primaryCauseId, setPrimaryCauseId ] = useState('')
  const [ explanation, setExplanation ] = useState('')
  const [ rootCause, setRootCause ] = useState('')
  const [ title, setTitle ] = useState('')
  const [ description, setDescription ] = useState('')
  const [ owner, setOwner ] = useState('')
  const [ dueDate, setDueDate ] = useState('')
  const minimumDueDate = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().slice(0, 10)
  }, [])

  useEffect(() => {
    setPrimaryCauseId(failure?.primaryCause || failure?.causeSnapshot?.id || '')
    setExplanation(failure?.explanation || '')
    setRootCause(failure?.rootCause || '')
    setTitle('')
    setDescription('')
    setOwner('')
    setDueDate('')
  }, [ failure?._id, failure?.status ])

  const enabledCatalog = useMemo(() => catalog.filter((row) => row.enabled && row.cnc), [ catalog ])
  const analysisComplete = Boolean(primaryCauseId && explanation.trim() && rootCause.trim())
  const actionComplete = Boolean(title.trim() && description.trim() && owner && dueDate)
  const analyzed = failure && failure.status !== 'pending_analysis'

  const submitAnalysis = async () => {
    if (!analysisComplete) return
    await onAnalyze({ primaryCauseId, explanation: explanation.trim(), rootCause: rootCause.trim() })
  }

  const submitAction = async () => {
    if (!actionComplete) return
    await onCreateAction({ title: title.trim(), description: description.trim(), owner, dueDate })
  }

  return (
    <Modal
      show={show}
      dialogClassName="programmation-manager-modal weekly-failure-analysis-modal"
      onHide={() => !saving && onHide()}
      keyboard={!saving}
      {...(saving ? { backdrop: 'static' } : {})}
    >
      <Modal.Header>
        <div className="weekly-failure-analysis-modal__navigator">
          <i className="fas fa-search" aria-hidden="true" />
          <div>
            <strong>{t('WEEKLY_PLAN:FailureAnalysisTitle')}</strong>
            <small>{taskName}</small>
          </div>
        </div>
        <button type="button" className="modal-options-button" aria-label={t('WEEKLY_PLAN:CloseAnalysis')} onClick={onHide} disabled={saving}>
          {saving ? <Spinner height={24} width={24} /> : <Times height={24} width={24} />}
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="programmation-manager-body-scrollable scroller">
          <Row>
            <Col lg={7}>
              <div className="programmation-manager-desktop-scrollable scroller">
                <ContentBlock title={t('WEEKLY_PLAN:CncAnalysisSection')}>
                  <Form>
                    <Form.Group controlId="weekly-failure-primary-cause">
                      <Form.Label>{t('WEEKLY_PLAN:PrimaryCause')}</Form.Label>
                      <Form.Control as="select" value={primaryCauseId} disabled={Boolean(analyzed) || saving} onChange={(event: any) => setPrimaryCauseId(event.target.value)}>
                        <option value="">{t('WEEKLY_PLAN:SelectPrimaryCause')}</option>
                        {causeGroups.map(([ type, label ]) => {
                          const rows = enabledCatalog.filter((row) => row.cnc.type === type)
                          if (!rows.length) return null
                          return (
                            <optgroup key={type} label={t(label)}>
                              {rows.map((row) => <option key={row.cnc._id} value={row.cnc._id}>{row.cnc.code} · {row.cnc.name}</option>)}
                            </optgroup>
                          )
                        })}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group controlId="weekly-failure-explanation">
                      <Form.Label>{t('WEEKLY_PLAN:FailureExplanation')}</Form.Label>
                      <Form.Control as="textarea" rows={3} value={explanation} readOnly={Boolean(analyzed)} disabled={saving} onChange={(event: any) => setExplanation(event.target.value)} />
                    </Form.Group>
                    <Form.Group controlId="weekly-failure-root-cause">
                      <Form.Label>{t('WEEKLY_PLAN:RootCause')}</Form.Label>
                      <Form.Control as="textarea" rows={3} value={rootCause} readOnly={Boolean(analyzed)} disabled={saving} onChange={(event: any) => setRootCause(event.target.value)} />
                    </Form.Group>
                    {!analyzed && (
                      <button type="button" className="btn btn-primary" disabled={saving || !analysisComplete} onClick={submitAnalysis}>
                        {saving ? t('WEEKLY_PLAN:SavingAnalysis') : t('WEEKLY_PLAN:SaveAnalysis')}
                      </button>
                    )}
                  </Form>
                </ContentBlock>
              </div>
            </Col>

            <Col lg={5}>
              <div className="programmation-manager-desktop-scrollable scroller">
                <ContentBlock title={t('WEEKLY_PLAN:CorrectiveActionSection')}>
                  {!analyzed && <p className="weekly-failure-analysis-modal__hint">{t('WEEKLY_PLAN:AnalyzeBeforeAction')}</p>}
                  {failure?.status === 'action_open' && <p className="weekly-failure-analysis-modal__success">{t('WEEKLY_PLAN:ActionRegistered')}</p>}
                  {analyzed && failure?.status !== 'action_open' && (
                    <Form>
                      <Form.Group controlId="weekly-action-title">
                        <Form.Label>{t('WEEKLY_PLAN:ActionTitle')}</Form.Label>
                        <Form.Control value={title} disabled={saving} onChange={(event: any) => setTitle(event.target.value)} />
                      </Form.Group>
                      <Form.Group controlId="weekly-action-description">
                        <Form.Label>{t('WEEKLY_PLAN:ActionDescription')}</Form.Label>
                        <Form.Control as="textarea" rows={3} value={description} disabled={saving} onChange={(event: any) => setDescription(event.target.value)} />
                      </Form.Group>
                      <Form.Group controlId="weekly-action-owner">
                        <Form.Label>{t('WEEKLY_PLAN:ActionOwner')}</Form.Label>
                        <Form.Control as="select" value={owner} disabled={saving} onChange={(event: any) => setOwner(event.target.value)}>
                          <option value="">{t('WEEKLY_PLAN:SelectActionOwner')}</option>
                          {members.map((member: any) => (
                            <option key={member._id} value={member._id}>{[ member.name, member.surname ].filter(Boolean).join(' ') || member.email}</option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId="weekly-action-due-date">
                        <Form.Label>{t('WEEKLY_PLAN:ActionDueDate')}</Form.Label>
                        <Form.Control type="date" min={minimumDueDate} value={dueDate} disabled={saving} onChange={(event: any) => setDueDate(event.target.value)} />
                      </Form.Group>
                      <button type="button" className="btn btn-primary" disabled={saving || !actionComplete} onClick={submitAction}>
                        {saving ? t('WEEKLY_PLAN:SavingAction') : t('WEEKLY_PLAN:CreateAction')}
                      </button>
                    </Form>
                  )}
                </ContentBlock>
              </div>
            </Col>
          </Row>
          {error && <div className="weekly-failure-analysis-modal__error" role="alert">{error}</div>}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default WeeklyFailureAnalysisModal
